import { z } from 'zod';

/**
 * Number pools (0137, owner 27 September 2026): a named group of the account's numbers that a
 * campaign shows as its caller ID, rotating in order or at random, a new number every call, hour,
 * twelve hours, day, two days or week.
 */

export const POOL_PERIODS = ['call', 'hour', '12h', 'day', '2d', 'week'] as const;
export type PoolPeriod = (typeof POOL_PERIODS)[number];
export const POOL_PERIOD_LABEL: Record<PoolPeriod, string> = {
  call: 'Every call', hour: 'Every hour', '12h': 'Twice a day', day: 'Every day', '2d': 'Every 2 days', week: 'Every week',
};

export const NumberPoolSchema = z.object({
  name: z.string().trim().min(1).max(60),
  mode: z.enum(['rotate', 'random']).default('rotate'),
  period: z.enum(POOL_PERIODS).default('day'),
  numbers: z.array(z.string().regex(/^\+\d{6,15}$/)).min(1).max(200),
  /** Calls back on the pool's numbers go to this queue (0138); null leaves them unrouted. */
  return_queue_id: z.string().uuid().nullable().default(null),
}).strict().superRefine((p, issue) => {
  if (p.mode === 'rotate' && p.period === 'call') issue.addIssue({ code: z.ZodIssueCode.custom, path: ['period'], message: 'Rotating changes number by the hour, day or week; "every call" is for random.' });
  if (new Set(p.numbers).size !== p.numbers.length) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['numbers'], message: 'Each number once.' });
});
export type NumberPoolInput = z.infer<typeof NumberPoolSchema>;

export interface NumberPool extends NumberPoolInput {
  id: string;
  /** Campaigns showing it. */
  campaigns: Array<{ id: string; name: string }>;
  /** The number it gives now (null when that is chosen per call). */
  current: string | null;
  /** The route it keeps for calls back (0138), and the queue's name. */
  return_queue_name: string | null;
  updated_at: string;
}

/** One of the account's numbers, and whether a pool may take it (0138). */
export interface PoolNumberOption {
  e164: string;
  /** The inbound route it is on, unless that is a pool's own. */
  route: string | null;
  /** The pool it is in. */
  pool: { id: string; name: string } | null;
}

const PERIOD_SEC: Record<Exclude<PoolPeriod, 'call'>, number> = {
  hour: 3600, '12h': 43_200, day: 86_400, '2d': 172_800, week: 604_800,
};

/** Seconds from 1970 on the account's own clock, so a "day" turns at its midnight. */
function localSeconds(now: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) / 1000;
}

/** A small, stable hash: the same pool and period always pick the same number. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * The number a pool gives at `now`. Rotate: in order, one per period, periods counted on the
 * account's clock (weeks from Monday). Random: a number chosen for the period, never the one the
 * period before used when there are two or more. Every call: null -- the dialler picks per call.
 */
export function poolNumber(pool: { id: string; mode: 'rotate' | 'random'; period: PoolPeriod; numbers: readonly string[] },
  now: Date, tz: string): string | null {
  const n = pool.numbers.length;
  if (n === 0) return null;
  if (pool.period === 'call') return null;
  // 1970-01-01 was a Thursday: three days on, periods of a week start on Monday.
  const t = localSeconds(now, tz) + (pool.period === 'week' ? 3 * 86_400 : 0);
  const k = Math.floor(t / PERIOD_SEC[pool.period]);
  if (pool.mode === 'rotate') return pool.numbers[((k % n) + n) % n]!;
  // Random: each run of n periods uses every number once, in a shuffled order of its own, and a
  // run never starts on the number the run before ended on -- random, fair, and never twice running.
  // Two numbers, never the same twice running: they take turns.
  if (n === 2) return pool.numbers[(((k + hash(pool.id)) % 2) + 2) % 2]!;
  const c = Math.floor(k / n);
  const order = (run: number): number[] => {
    const a = shuffled(n, `${pool.id}:${run}`);
    if (n > 1) {
      const before = shuffled(n, `${pool.id}:${run - 1}`);
      if (a[0] === before[n - 1]) [a[0], a[1]] = [a[1]!, a[0]!];
    }
    return a;
  };
  return pool.numbers[order(c)[((k % n) + n) % n]!]!;
}

/** 0..n-1 in an order fixed by `seed` (Fisher-Yates on a seeded generator). */
function shuffled(n: number, seed: string): number[] {
  let x = hash(seed) || 1;
  const rnd = () => { x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0; return x / 4294967296; };
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}
